import React, { useEffect, useState } from 'react'
import { Box, Chip, IconButton, Tooltip } from '@mui/material'
import {
    Wifi,
    WifiOff,
    Refresh,
    Error as ErrorIcon,
    CheckCircle,
    Warning
} from '@mui/icons-material'
import useLockStore from '../stores/useLockStore'

const ConnectionStatus = () => {
    const {
        connected,
        error,
        reconnectAttempts,
        lastConnectionTime,
        checkConnectionHealth,
        connect,
        disconnect,
        forceReconnect
    } = useLockStore()

    const [showDetails, setShowDetails] = useState(false)
    const [healthCheckInterval, setHealthCheckInterval] = useState(null)

    // Periodic health check - more frequent to catch stuck states
    useEffect(() => {
        const interval = setInterval(() => {
            const isHealthy = checkConnectionHealth()
            // Only trigger force reconnect if we're not connected and not already reconnecting
            if (!isHealthy && !connected && !reconnectAttempts) {
                console.log('🔧 Health check failed - triggering force reconnect')
                forceReconnect()
            }
        }, 10000) // Check every 10 seconds instead of 30

        setHealthCheckInterval(interval)

        return () => {
            if (interval) clearInterval(interval)
        }
    }, [checkConnectionHealth, connected, reconnectAttempts, forceReconnect])

    const getStatusColor = () => {
        if (connected) return 'success'
        if (reconnectAttempts > 0) return 'warning'
        return 'error'
    }

    const getStatusIcon = () => {
        if (connected) return <CheckCircle />
        if (reconnectAttempts > 0) return <Warning />
        return <ErrorIcon />
    }

    const getStatusText = () => {
        if (connected) return 'Connected'
        if (reconnectAttempts > 0) return `Reconnecting (${reconnectAttempts}/5)`
        return 'Disconnected'
    }

    const handleReconnect = () => {
        console.log('🔄 Manual reconnection triggered from ConnectionStatus')
        forceReconnect()
        // The connect function will be called from the parent component
        // with the proper token and parameters
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
                title={
                    <Box>
                        <div>Status: {getStatusText()}</div>
                        {lastConnectionTime && <div>Last connected: {new Date(lastConnectionTime).toLocaleString()}</div>}
                        {error && <div>Error: {error}</div>}
                        {reconnectAttempts > 0 && <div>Reconnect attempts: {reconnectAttempts}/5</div>}
                    </Box>
                }
                arrow
            >
                <Chip
                    icon={getStatusIcon()}
                    label={getStatusText()}
                    color={getStatusColor()}
                    size="small"
                    variant="outlined"
                    onClick={() => setShowDetails(!showDetails)}
                />
            </Tooltip>

            {!connected && (
                <Tooltip title="Force reconnect">
                    <IconButton
                        size="small"
                        onClick={handleReconnect}
                        color="primary"
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    )
}

export default ConnectionStatus
